import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { useEffect, useRef } from 'react';

/** The thumbnail is a proof of identity, not a reading surface — rendered at
 *  the width it is displayed at, doubled for a retina screen. Anything larger
 *  is paying full-page render cost for a picture the size of a business card. */
const THUMBNAIL_WIDTH = 320;
const maxPixelRatio = 2;

interface DocumentThumbnailProps {
  document: PDFDocumentProxy;
  /** Describes the page to a screen reader, which cannot see the render. */
  alt: string;
}

/**
 * The first page of a document, drawn small.
 *
 * Deliberately not `PdfPage`: that component fits a page to the width it is
 * given and reports its own aspect ratio so a scrolling column can reserve
 * height. A thumbnail wants the opposite — a fixed frame the page is cropped
 * into, so a stack of cards keeps one height whatever shape the documents are.
 *
 * The top of the page is what is kept when it overflows. A statutory form
 * carries its identity in the letterhead and the title block, so the crop that
 * tells a director which document this is, is the crop that starts at the top.
 */
export const DocumentThumbnail = ({ document, alt }: DocumentThumbnailProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let isCancelled = false;
    let task: RenderTask | undefined;

    const render = async () => {
      const page = await document.getPage(1);
      const canvas = canvasRef.current;

      if (isCancelled || canvas === null) return;

      const unscaled = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: THUMBNAIL_WIDTH / unscaled.width });

      const context = canvas.getContext('2d');
      if (context === null) return;

      const pixelRatio = Math.min(window.devicePixelRatio, maxPixelRatio);
      canvas.width = Math.floor(viewport.width * pixelRatio);
      canvas.height = Math.floor(viewport.height * pixelRatio);
      canvas.style.width = '100%';

      task = page.render({
        canvas,
        canvasContext: context,
        viewport,
        transform: [pixelRatio, 0, 0, pixelRatio, 0, 0],
      });

      try {
        await task.promise;
      } catch {
        // A cancelled render is the card unmounting mid-paint. The frame it
        // would have filled is already gone; there is nothing to report.
      }
    };

    void render();

    return () => {
      isCancelled = true;
      task?.cancel();
    };
  }, [document]);

  return <canvas ref={canvasRef} aria-label={alt} role="img" className="block w-full" />;
};
